/**
 * Validation utilities with Zod integration for MercNet ecosystem
 */

import { z } from 'zod'
import { MercNetError } from '../utils/index.js'

// Base validation schemas
export const baseEntitySchema = z.object({
  id: z.string().min(1, 'ID is required'),
  createdAt: z.date(),
  updatedAt: z.date()
})

export const paginationParamsSchema = z.object({
  page: z.number().int().positive().optional().default(1),
  limit: z.number().int().positive().max(100).optional().default(20),
  offset: z.number().int().nonnegative().optional()
})

export const apiResponseSchema = <T extends z.ZodType>(dataSchema: T) =>
  z.object({
    data: dataSchema.optional(),
    error: z
      .object({
        code: z.string(),
        message: z.string(),
        details: z.record(z.unknown()).optional()
      })
      .optional(),
    success: z.boolean(),
    message: z.string().optional()
  })

// Common field validators
export const validators = {
  email: z.string().email('Invalid email format'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  url: z.string().url('Invalid URL format'),
  phone: z.string().regex(/^\+?[1-9]\d{1,14}$/, 'Invalid phone number format'),
  uuid: z.string().uuid('Invalid UUID format'),
  slug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Invalid slug format'),
  hex: z.string().regex(/^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, 'Invalid hex color'),

  // Date validators
  dateString: z.string().datetime('Invalid date format'),
  dateRange: z
    .object({
      start: z.date(),
      end: z.date()
    })
    .refine(data => data.end > data.start, {
      message: 'End date must be after start date',
      path: ['end']
    }),

  // Number validators
  positiveNumber: z.number().positive('Must be a positive number'),
  nonNegativeNumber: z.number().nonnegative('Must be a non-negative number'),
  percentage: z.number().min(0).max(100, 'Must be between 0 and 100'),
  currency: z.number().multipleOf(0.01, 'Invalid currency format'),

  // String validators
  nonEmptyString: z.string().min(1, 'Cannot be empty'),
  alphanumeric: z.string().regex(/^[a-zA-Z0-9]+$/, 'Must contain only letters and numbers'),
  noWhitespace: z.string().regex(/^\S+$/, 'Cannot contain whitespace')
}

// Environment validation
export const environmentSchema = z.enum(['development', 'production', 'test', 'staging'])

export const baseConfigSchema = z.object({
  environment: environmentSchema,
  apiUrl: validators.url,
  graphqlUrl: validators.url.optional(),
  debug: z.boolean().optional().default(false)
})

// Validation helper functions
export class ValidationError extends MercNetError {
  constructor(
    message: string,
    public issues: z.ZodIssue[]
  ) {
    super(message, 'VALIDATION_ERROR', { issues })
    this.name = 'ValidationError'
  }
}

export const validate = <T>(
  schema: z.ZodSchema<T>,
  data: unknown,
  options?: {
    throwOnError?: boolean
    errorMessage?: string
  }
): { success: true; data: T } | { success: false; error: ValidationError } => {
  const result = schema.safeParse(data)

  if (result.success) {
    return { success: true, data: result.data }
  }

  const error = new ValidationError(
    options?.errorMessage || 'Validation failed',
    result.error.issues
  )

  if (options?.throwOnError) {
    throw error
  }

  return { success: false, error }
}

export const validateAndThrow = <T>(
  schema: z.ZodSchema<T>,
  data: unknown,
  errorMessage?: string
): T => {
  const result = validate(schema, data, { throwOnError: true, errorMessage })
  return (result as { success: true; data: T }).data
}

// Schema composition utilities
export const createArraySchema = <T extends z.ZodType>(
  itemSchema: T,
  options?: {
    minLength?: number
    maxLength?: number
    unique?: boolean
  }
): z.ZodArray<T> | z.ZodEffects<z.ZodArray<T>, z.infer<T>[], z.infer<T>[]> => {
  let schema: z.ZodArray<T> | z.ZodEffects<z.ZodArray<T>, z.infer<T>[], z.infer<T>[]> =
    z.array(itemSchema)

  if (options?.minLength !== undefined) {
    schema = schema.min(options.minLength, `Must have at least ${options.minLength} items`)
  }

  if (options?.maxLength !== undefined) {
    schema = schema.max(options.maxLength, `Must have at most ${options.maxLength} items`)
  }

  if (options?.unique) {
    schema = schema.refine(
      items => {
        const set = new Set(items.map(item => JSON.stringify(item)))
        return set.size === items.length
      },
      { message: 'Items must be unique' }
    )
  }

  return schema
}

export const createOptionalWithDefault = <T extends z.ZodType>(
  schema: T,
  defaultValue: z.infer<T>
) => {
  return schema.optional().default(defaultValue)
}

// Transform utilities
export const transforms = {
  stringToNumber: z.string().transform((val, ctx) => {
    const parsed = parseFloat(val)
    if (isNaN(parsed)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Not a number'
      })
      return z.NEVER
    }
    return parsed
  }),

  stringToBoolean: z.string().transform(val => {
    return val.toLowerCase() === 'true' || val === '1'
  }),

  stringToDate: z.string().transform((val, ctx) => {
    const date = new Date(val)
    if (isNaN(date.getTime())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid date'
      })
      return z.NEVER
    }
    return date
  }),

  trimString: z.string().transform(val => val.trim()),

  toLowerCase: z.string().transform(val => val.toLowerCase()),

  toUpperCase: z.string().transform(val => val.toUpperCase())
}

// Conditional validation
export const createConditionalSchema = <T extends z.ZodRawShape>(
  baseSchema: z.ZodObject<T>,
  conditions: Array<{
    when: (data: z.infer<z.ZodObject<T>>) => boolean
    then: z.ZodSchema
    message?: string
  }>
) => {
  return baseSchema.superRefine((data, ctx) => {
    for (const condition of conditions) {
      if (condition.when(data)) {
        const result = condition.then.safeParse(data)
        if (!result.success) {
          result.error.issues.forEach(issue => {
            ctx.addIssue({
              ...issue,
              message: condition.message || issue.message
            })
          })
        }
      }
    }
  })
}

// Custom validation rules
export const customValidators = {
  strongPassword: z.string().refine(
    password => {
      const hasUpperCase = /[A-Z]/.test(password)
      const hasLowerCase = /[a-z]/.test(password)
      const hasNumbers = /\d/.test(password)
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password)
      return hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar
    },
    {
      message: 'Password must contain uppercase, lowercase, number and special character'
    }
  ),

  uniqueArray: <T>(itemSchema: z.ZodSchema<T>, keyExtractor?: (item: T) => any) =>
    z.array(itemSchema).refine(
      items => {
        if (!keyExtractor) {
          return new Set(items).size === items.length
        }
        const keys = items.map(keyExtractor)
        return new Set(keys).size === keys.length
      },
      { message: 'Array items must be unique' }
    ),

  futureDate: z.date().refine(date => date > new Date(), { message: 'Date must be in the future' }),

  pastDate: z.date().refine(date => date < new Date(), { message: 'Date must be in the past' })
}

// Export commonly used schemas with different names to avoid conflicts
export {
  z,
  baseEntitySchema as BaseEntitySchema,
  paginationParamsSchema as PaginationParamsSchema,
  apiResponseSchema as ApiResponseSchema,
  environmentSchema as EnvironmentSchema,
  baseConfigSchema as BaseConfigSchema
}
